import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  DialogActions,
  Dialog,
  Box,
  DialogTitle,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrashAlt,
  faUserPlus,
  faEnvelope,
  faUser,
  faCalendarAlt,
  faClock,
  faBell,
  faKey,
  faUndo,
  faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";

import { useAuth } from "../../contexts/AuthContext";
import UserManagementService from "../../services/userManagementService";
import { User } from "../../models/User";
import UserDialogContent from "../../components/Admin/AddAdminDialog";
import useNotification from "../../hooks/useNotification";

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  receiveEmailNotifications: boolean;
  receiveContactFormEmails: boolean;
}

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
}

const UserManagementPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    email: "",
    firstName: "",
    lastName: "",
    receiveEmailNotifications: true,
    receiveContactFormEmails: true,
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    getUsers();
  }, []);

  const getUsers = async () => {
    try {
      const response = await UserManagementService.fetchUsers();
      setUsers(response);
    } catch (error) {
      console.error("Error fetching users:", error);
      showNotification(
        "Failed to fetch users. Please try again later.",
        "error"
      );
    }
  };

  const handleOpenDialog = (user: User | null = null) => {
    setCurrentUser(user);
    setFormData(
      user || {
        email: "",
        firstName: "",
        lastName: "",
        receiveEmailNotifications: true,
        receiveContactFormEmails: true,
      }
    );
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentUser(null);
    setFormErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    validateField(name, value);
  };
  const validateField = (name: string, value: string) => {
    let errors = { ...formErrors };
    switch (name) {
      case "email":
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        errors.email = re.test(value)
          ? undefined
          : "Please enter a valid email address";
        break;
      case "firstName":
        errors.firstName = value.trim()
          ? undefined
          : "First name cannot be blank";
        break;
      case "lastName":
        errors.lastName = value.trim()
          ? undefined
          : "Last name cannot be blank";
        break;
    }
    setFormErrors(errors);
  };

  const validateForm = () => {
    let errors: FormErrors = {};
    if (!formData.email.trim()) errors.email = "Email is required";
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      if (currentUser) {
        await UserManagementService.updateUser(currentUser.adminID, formData);
        showNotification("Administrator updated successfully", "success");
      } else {
        await UserManagementService.addUser(formData);
        showNotification("Administrator added successfully", "success");
      }
      getUsers();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving user:", error);
      //if error is 400, show notification that the email is already in use
      if (error instanceof Error && error.message.includes("400")) {
        showNotification("Email already in use. Please try again.", "error");
      } else {
        showNotification(
          "Failed to save Administrator. Please try again.",
          "error"
        );
      }
    }
  };

  const handleArchiveUser = async (user: User) => {
    if (
      window.confirm("Are you sure you want to archive this Administrator?")
    ) {
      try {
        await UserManagementService.archiveUser(user.adminID);
        getUsers();
        showNotification("Administrator archived successfully", "success");
      } catch (error) {
        console.error("Error archiving user:", error);
        showNotification("Failed to archive Administrator", "error");
      }
    }
  };
  const handleRestoreUser = async (userId: number) => {
    if (window.confirm("Are you sure you want to restore this user?")) {
      try {
        await UserManagementService.restoreUser(userId);
        getUsers();
        showNotification("User restored successfully", "success");
      } catch (error) {
        console.error("Error restoring user:", error);
        showNotification("Failed to restore user. Please try again.", "error");
      }
    }
  };

  const handleResetPassword = async (user: User) => {
    try {
      await UserManagementService.initiatePasswordReset(user.email);
      showNotification("Password reset email sent successfully", "success");
    } catch (error) {
      console.error("Error resetting password:", error);
      showNotification("Failed to reset password. Please try again.", "error");
    }
  };

  const handleResendInvitation = async (userId: number) => {
    try {
      await UserManagementService.resendInvitation(userId);
      showNotification("Invitation email resent successfully", "success");
    } catch (error) {
      console.error("Error resending invitation:", error);
      showNotification(
        "Failed to resend invitation. Please try again.",
        "error"
      );
    }
  };

  return (
    <Container>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          <FontAwesomeIcon icon={faUser} /> Administrator Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FontAwesomeIcon icon={faUserPlus} />}
          onClick={() => handleOpenDialog()}
          sx={{ mb: 2 }}
        >
          Add New Administrator
        </Button>
        {users && users.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FontAwesomeIcon icon={faEnvelope} /> Email
                  </TableCell>
                  <TableCell>
                    <FontAwesomeIcon icon={faUser} /> First Name
                  </TableCell>
                  <TableCell>
                    <FontAwesomeIcon icon={faUser} /> Last Name
                  </TableCell>
                  <TableCell>
                    <FontAwesomeIcon icon={faCalendarAlt} /> Date Created
                  </TableCell>
                  <TableCell>
                    <FontAwesomeIcon icon={faClock} /> Last Login
                  </TableCell>
                  <TableCell>
                    <FontAwesomeIcon icon={faBell} /> Status
                  </TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.adminID}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>
                      {new Date(user.dateCreated).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : "Never"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.disabled ? "Archived" : "Active"}
                        color={user.disabled ? "default" : "success"}
                      />
                    </TableCell>
                    <TableCell>
                      {!user.disabled && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton onClick={() => handleOpenDialog(user)}>
                              <FontAwesomeIcon icon={faEdit} size="sm" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              onClick={() => handleResetPassword(user)}
                            >
                              <FontAwesomeIcon icon={faKey} size="sm" />
                            </IconButton>
                          </Tooltip>
                          {!user.lastLogin && (
                            <Tooltip title="Resend Invitation">
                              <IconButton
                                onClick={() =>
                                  handleResendInvitation(user.adminID)
                                }
                              >
                                <FontAwesomeIcon
                                  icon={faPaperPlane}
                                  size="sm"
                                />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Archive">
                            <IconButton onClick={() => handleArchiveUser(user)}>
                              <FontAwesomeIcon icon={faTrashAlt} size="sm" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {user.disabled && (
                        <Tooltip title="Restore">
                          <IconButton
                            onClick={() => handleRestoreUser(user.adminID)}
                          >
                            <FontAwesomeIcon icon={faUndo} size="sm" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body1" gutterBottom>
            No Administrators found.
          </Typography>
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>
            <FontAwesomeIcon icon={currentUser ? faEdit : faUserPlus} />{" "}
            {currentUser ? "Edit Administrator" : "Add New Administrator"}
          </DialogTitle>
          <UserDialogContent
            formData={formData}
            handleInputChange={handleInputChange}
            formErrors={formErrors}
          />
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={Object.values(formErrors).some(
                (error) => error !== undefined
              )}
            >
              {currentUser ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default UserManagementPage;
